import { observer } from 'mobx-react-lite'
import type { PhysicalAddressEditorViewModel } from '@/features/tax-profile/PhysicalAddressEditorViewModel'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { FormFieldGroup } from '@/components/Form'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import { COUNTRIES, COUNTRY_US } from '@/assets/data/countries'
import { US_STATES_AND_TERRITORIES } from '@/assets/data/us-states-territories'

export interface PhysicalAddressEditorProps {
  vm: PhysicalAddressEditorViewModel
  testId?: string
}

/**
 * The PhysicalAddressEditor
 */
export const PhysicalAddressEditor = observer(function PhysicalAddressEditor({
  vm,
  testId,
}: PhysicalAddressEditorProps) {
  return (
    <>
      <TextFieldContainer
        model={vm}
        field="addressLine1"
        label="Address line 1"
        testId={testId ? `${testId}-line1-input` : undefined}
      />
      <TextFieldContainer
        model={vm}
        field="addressLine2"
        label="Address line 2"
        testId={testId ? `${testId}-line2-input` : undefined}
      />
      <FormFieldGroup>
        <TextFieldContainer
          model={vm}
          field="city"
          label="City"
          testId={testId ? `${testId}-city-input` : undefined}
        />
        <TextFieldContainer
          model={vm}
          field="postalCode"
          label="Zip / postal code"
          testId={testId ? `${testId}-zip-code-input` : undefined}
        />
      </FormFieldGroup>
      <FormFieldGroup stackWhenCompact>
        <RegionInputContainer vm={vm} testId={testId} />
        <CountryInputContainer vm={vm} testId={testId} />
      </FormFieldGroup>
    </>
  )
})

interface RegionInputContainer {
  vm: PhysicalAddressEditorViewModel
  testId?: string
}

/**
 * Input for region.
 */
export const RegionInputContainer = observer(
  function UsStateTerritorySelectContainer({
    vm,
    testId,
  }: RegionInputContainer) {
    // We're using display names rather than code throughout.
    if (!vm.country || vm.country === COUNTRY_US[1]) {
      return (
        <SelectContainer
          label="State or territory"
          model={vm}
          field="region"
          autoComplete="billing address-level1"
          items={US_STATES_AND_TERRITORIES}
          getKey={([_, label]) => label}
          getLabel={([_, label]) => label}
          testId={testId ? `${testId}-state-select` : undefined}
        />
      )
    }

    return (
      <TextFieldContainer
        label="Region"
        model={vm}
        field="region"
        autoComplete="billing address-level1"
        testId={testId ? `${testId}-region-input` : undefined}
      />
    )
  },
)

interface CountryInputContainerProps {
  vm: PhysicalAddressEditorViewModel
  testId?: string
}

/**
 * Select for supported countries.
 */
export const CountryInputContainer = observer(function CountryInputContainer({
  vm,
  testId,
}: CountryInputContainerProps) {
  return (
    <SelectContainer
      label="Country"
      model={vm}
      field="country"
      items={COUNTRIES}
      getKey={([_, label]) => label}
      getLabel={([_, label]) => label}
      testId={testId ? `${testId}-country-select` : undefined}
    />
  )
})
